import { useRef } from 'react';

import type {
  IUseTransferActionsTrackingParams,
  TTransferActionTypes,
  TTransferFileFormat,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer';
import type { TReportTemplateTypes } from '@app/dashboard/types/shared/modals/reports-template';
import {
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
  BALANCE_TRANSFERS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import {
  REPORT_FILE_TYPES,
  TRANSFER_ACTIONS,
} from '@app/dashboard/constants/balance_and_transfers/single-transfer';

import { useTransferActions } from './useTransferActions';
import { transferActionsEventTracking } from '../utils/events-tracking';

export const useTransferActionsTracking = ({
  transferId,
  header,
  transactionCount,
}: IUseTransferActionsTrackingParams) => {
  const { exportTransferReport, downloadAch } = useTransferActions();

  const clickedReportTypeRef = useRef<TTransferFileFormat | null>(null);

  const handleActionTrackerEvent = (
    action: TTransferActionTypes,
    extraEventProperties: Record<string, string | number> = {}
  ) => {
    const formatId = `#TRF-${transferId}`;

    transferActionsEventTracking({
      action,
      sharedProperties: {
        transferId: formatId,
        status: header?.status?.key ?? '',
        currency: header?.currency ?? '',
      },
      extraEventProperties: {
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.ENTRY_POINT]:
          BALANCE_TRANSFERS_EVENTS_VALUES.QUICK_VIEW,
        ...extraEventProperties,
      },
    });
  };

  const handleDownloadTransactionsReport = (
    fileFormat: TTransferFileFormat,
    templateType: TReportTemplateTypes
  ) => {
    clickedReportTypeRef.current = fileFormat;
    const action =
      fileFormat === REPORT_FILE_TYPES.PDF
        ? TRANSFER_ACTIONS.DOWNLOAD_TRANSACTIONS_PDF
        : TRANSFER_ACTIONS.DOWNLOAD_TRANSACTIONS_CSV;

    handleActionTrackerEvent(action, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE
        .TRANSACTION_COUNT]: transactionCount ?? '',
    });

    exportTransferReport.mutate({
      id: transferId as string,
      fileFormat,
      templateType,
    });
  };

  const handleDownloadAchDocument = (achDocumentUrl: string) => {
    handleActionTrackerEvent(TRANSFER_ACTIONS.DOWNLOAD_PROOF, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.NET_AMOUNT]:
        header?.amount ?? '',
    });
    downloadAch.mutate(achDocumentUrl);
  };

  return {
    handleDownloadTransactionsReport,
    handleDownloadAchDocument,
    clickedReportTypeRef,
    exportTransferReport,
    downloadAch,
  };
};
